// zmist's own consent record - same shape and localStorage key as the
// marketing site (landing/assets/cookie-consent.js), so a person's choice
// reads the same way regardless of which zmist surface they're on. The web
// app itself authenticates via localStorage (a JWT), not cookies, and sets
// no analytics/marketing cookies today - this exists so the *mechanism* is
// real and ready the moment that changes, instead of retrofitting consent-
// gating onto a script that's already running. Any future analytics/
// marketing script should check hasConsent(category) before it loads.
export const COOKIE_CONSENT_STORAGE_KEY = 'zmist_cookie_consent';
export const COOKIE_CONSENT_EVENT = 'zmist:cookie-consent';

export type ConsentCategory = 'analytics' | 'marketing';

export interface CookieConsent {
  essential: true;
  analytics: boolean;
  marketing: boolean;
  decidedAt: string;
}

export function readConsent(): CookieConsent | null {
  try {
    const raw = localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed as CookieConsent;
  } catch {
    return null;
  }
}

export function writeConsent(categories: { analytics: boolean; marketing: boolean }): CookieConsent {
  const value: CookieConsent = {
    essential: true,
    analytics: !!categories.analytics,
    marketing: !!categories.marketing,
    decidedAt: new Date().toISOString(),
  };
  try {
    localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Private-browsing/storage-disabled: consent can't persist, so the
    // banner just asks again next load - fails safe, never silently "on".
  }
  window.dispatchEvent(new CustomEvent(COOKIE_CONSENT_EVENT, { detail: value }));
  return value;
}

export function hasConsent(category: ConsentCategory): boolean {
  const c = readConsent();
  return !!(c && c[category]);
}
